import { DataTable, DataTableExpandedRows, DataTableValueArray, DataTableRowEditCompleteEvent,} from "primereact/datatable";
import { Dialog } from "primereact/dialog";
import { useEffect, useRef, useState } from "react";
import { ColumnDef, OrderDetail, OrderModel } from "../../models/orders/order-list-model";
import { useTranslation } from "react-i18next";
import { Column,ColumnEditorOptions } from "primereact/column";
import { Button } from "primereact/button";
import OrderReview from "../orders/order-review";
import { Toast, ToastMessage } from 'primereact/toast';
import CreateInvoice from "./create-invoice";
import InvoiceServices from "../../services/invoices/gen_inv.service";
import { mapInvoiceData } from "../../models/invoice/invoice-mapper-model";
import RouteConstant from "../../constants/route.constants";
import { useNavigate } from "react-router-dom";
import { useAppDispatch } from "../../redux/hooks";
import { GetWayLinkModel } from "../../models/getway-link/getway-link-model";
import { selectedGetway } from "../../redux/slices/getway-link";
import PaymentProcess from "./payment-process";
import OrderService from "../../services/order/order.service";
import { InputNumber,InputNumberValueChangeEvent } from "primereact/inputnumber";

interface DataTableProps {
  query: string;
  openOrder: boolean;
  onSearch: (query: string, openOrder: boolean) => void;
  data: any[];
}

const InvoicesDataTable: React.FC<DataTableProps> = ({ data, onSearch, query, openOrder }) => {

  const { t } = useTranslation();
  const toast = useRef<Toast>(null);
  const [dataRecords, setDataRecords] = useState<OrderModel[]>(data);
  const [detailVisible, setDetailVisible] = useState(false);  
  const [selectedOrder, setSelectedOrder] = useState<OrderModel | null>(null);
  const [editVisible, setEditVisible] = useState(false);
  const [editOrder, setEditOrder] = useState<OrderModel | null>(null);
  const [expandedRows, setExpandedRows] = useState<DataTableExpandedRows | undefined>(undefined);
  const [paymentVisible, setPaymentVisible] = useState(false);
  const [paymentOrder, setPaymentOrder] = useState<OrderModel | null>(null);
  const [refreshDataTableKey, setRefreshDataTableKey] = useState(0); // Key to trigger re-render the data table
  var navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [newPendingQuantity, setNewPendingQuantity] = useState<{ [key: string]: number }>({});



  const showMessage = (event: React.MouseEvent<HTMLButtonElement>, message: string, severity: ToastMessage['severity']) => {
    const target = event.target as HTMLButtonElement;
    const label = target.innerText;
    toast.current?.show({ severity: severity, summary: label, detail: message, life: 3000 });
  };

  const showMessageForOtherPayment = (data: any, message: string, severity: ToastMessage['severity']) => {
    const label = "Proceed to Payment";
    toast.current?.show({ severity: severity, summary: label, detail: message, life: 3000 });
  };

  const showSimpleToastMessage = (label: string, message: string, severity: ToastMessage["severity"]) => {
    toast.current?.show({severity: severity, summary: label, detail: message,life: 3000,});
  };

  const handleSetOrderData = (rowData: OrderModel) => {
    setSelectedOrder(rowData);
    setDetailVisible(true);
  };

  const handleSetEditInvoice = (rowData: OrderModel) => {
    setEditOrder(rowData);
    setEditVisible(true);
  };

  const handleGetPayment = (rowData: OrderModel) => {
    setPaymentOrder(rowData);
    setPaymentVisible(true);
  };

  const handleCreateInvoice = async (event: any, rowData: OrderModel, payId: number) => {
    try
    {
      const checkoutPayId = rowData.amountPaid>0?0:payId;
      const invoiceData = mapInvoiceData(rowData, checkoutPayId);
      let response = await InvoiceServices.GenerateNewInvoice(invoiceData);     
      if(response.id > 0) {       
        showMessage(event, 'Invoice #' + response.id +' generated successfully', 'success');
        setExpandedRows(undefined);
        onSearch(query,openOrder);    
        if(checkoutPayId == 7) {
          const getwayData = response.additionalInfo;
          dispatch(selectedGetway(mapAdditionalInfo(getwayData)));
          navigate(RouteConstant.getwayProcess);
        }
      }
      else{
        showMessage(event, response.message + ': Error in generate invoice', 'error');
      }
    }
    catch
    {
      showMessage(event, 'Error in generate invoice', 'error');
    }
  };

  const handlePayment = async (event: any, invId: number, payId: number) => {
    try
    {
      let response = await InvoiceServices.PaymentInvoice(invId, payId);    
      if(response.id > 0) {        
        showMessage(event, 'Payment generated successfully','success');        
        if(payId == 5) {
          const getwayData = response.additionalInfo;
          dispatch(selectedGetway(mapAdditionalInfo(getwayData)));
          navigate(RouteConstant.getwayProcess);
        }
        onSearch(query,openOrder);
      }
      else{
        showMessage(event, response.message + ': Error in invoice payment', 'error');
      }
    }
    catch
    {
      showMessage(event, 'Error in generate invoice payment', 'error');
    }
  };

  const handleOtherPaymentMode = (otherPaymentData: any) => {
    otherPaymentData.orderId = paymentOrder?.orderID.toString();
    InvoiceServices.OthersPayment(otherPaymentData)
      .then((response) => {
        if (response.id > 0) {
          showMessageForOtherPayment(otherPaymentData, 'Payment generated successfully', 'success');
          onSearch(query, openOrder);
        } else {
          showMessageForOtherPayment(otherPaymentData, response.message + ': Error in invoice payment', 'error');
        }
      })
      .catch(() => {
        showMessageForOtherPayment(otherPaymentData, 'Error in generate invoice payment', 'error');
      });
  };

  const mapAdditionalInfo = (data: any): GetWayLinkModel => {
    return {
      paymentURL: data.paymentURL,
      expectedDeliveryDate: data.expectedDeliveryDate,
    };
  };

  useEffect(() => {
    if (selectedOrder !== null)
    setDetailVisible(true);
  }, [selectedOrder]);

  useEffect(() => {
    if (editOrder !== null)
    setEditVisible(true);

    if(editOrder){
      setDataRecords(prevData => 
        prevData.map(order => 
            order.orderID === editOrder.orderID ? { ...editOrder } : order
        )
    );
    }
    setRefreshDataTableKey(prevKey => prevKey + 1); // Change state to force re-render the datatable
  }, [editOrder]);

 

  useEffect(() => {
    setDataRecords(data); 
  },[data]);

  const expandedColumns: ColumnDef[] = [
    {
      header: "Sl.",
      style: { minWidth: "1rem" },
      cell: (rowData: any, { rowIndex }: { rowIndex?: number } = {}) => rowIndex !== undefined ? rowIndex + 1 :"1",
  },
    {
      header: "Product Name",
      style: { minWidth: "10rem" },
      cell: (row: any) => row.productName,
    },
    {
      header: "Ordered Quantity",
      style: { minWidth: "8rem" },
      cell: (row: any) => row.quantity + " " + row.measurementTypeName
    },
    {
      header: "Pending Quantity",
      style: { minWidth: "8rem" },
      cell: (row: any) => (row.pendingQuantity>0?row.pendingQuantity:0) + " " + row.measurementTypeName
    }
  ];  
  
  const columns: ColumnDef[] = [
    {
      style: { minWidth: "2rem" },
      expander: (row: any) => row.orderStatusID === 1 || row.orderStatusID === 2 && isPendingQuantity(row.orderDetail)
    },    
    {      
      field: "orderID",
      header: t("orderlist_table_orderno"),
      style: { minWidth: "7rem" },
      cell: (row: any) => (
        <span
          style={{ color: "darkcyan", fontWeight: "bolder", cursor: "pointer" }}
          onClick={() => handleSetOrderData(row)}
        >
          {row.orderID}
        </span>
      ),
    },
    {
      field: "createdDate",
      header: t("orderlist_table_orderdate"),
      style: { minWidth: "9rem" },
      cell: (row: any) =>
        new Date(row.createdDate).toLocaleDateString("en-gb", {
          day: "2-digit",
          month: "short",
          year: "numeric",
        }),
    },
    {
      field: "orderStatusName",
      header: t("orderlist_table_orderstatus"),
      style: { minWidth: "10rem" },
      cell: (row: any) => row.orderStatusName,
    },
    {
      field: "userName",
      header: t("orderlist_table_username"),
      style: { minWidth: "10rem" },
      cell: (row: any) => row.userName,
    },
    {
      field: "primaryContactNbr",
      header: t("orderlist_table_mobile"),
      style: { minWidth: "8rem" },
      cell: (row: any) => row.primaryContactNbr,
    },    
    {
      field: "invoiceNumber",
      header: t("orderlist_table_invoiceno"),
      style: { minWidth: "8rem" },
      cell: (row: any) =>
        row.orderInvoice.length > 0 ? (
          <>
          <span style={{color: "darkcyan",fontWeight: "bolder",cursor: "pointer"}} onClick={() => handleSetOrderData(row)}>
            {row.orderInvoice[0].invoiceNo}
          </span>
          <span style={{color: 'green'}}>
            {row.orderInvoice.length>1 ? " (+" + String(row.orderInvoice.length-1) + ")" : ""}
          </span>
          </>
        ) : "",
    },
    {
      field: "invoiceDate",
      header: t("orderlist_table_invoicedate"),
      style: { minWidth: "10rem" },
      cell: (row: any) =>
        row.orderInvoice.length > 0 
          ? new Date(row.orderInvoice[0].invoicedDate).toLocaleDateString("en-gb", {
            day: "2-digit",
            month: "short",
            year: "numeric",
          })
          : "",
    },
    {
      header: "Route Code",
      style: { minWidth: "8rem" },
      cell: (row: any) => row.routeCodeName,
    },
    {
      style: { minWidth: "2rem" },
      header: "Action",
      cell: (row: any) =>
        //Added showPaymentButton() method to show payment btn only for online payment and payment link
        row.amountPaid === 0 && noPendingQuantity(row.orderDetail) && !showPaymentButton(row.orderInvoice) ? (
          <>          
          <Button className="border-round" onClick={() => handleGetPayment(row)} severity="info">
            Payment
          </Button>
          </>
        ) : "",
    }
  ];

  const isPendingQuantity = (items: OrderDetail[]): boolean => {    
    return items.some((item) => item.pendingQuantity > 0);
  }

  const noPendingQuantity = (items: OrderDetail[]): boolean => {    
    return items.every((item) => item.pendingQuantity <= 0);
  }

  //Added showPaymentButton() method to show payment btn only for online payment and payment link
  const showPaymentButton = (invoices:any) : boolean => {
   return invoices.every((invoice:any) => invoice.paymentModeID === 1 && invoice.amountPaid === 0 ? true : false)
  }

  const hideDetailDialog = () => {
    setDetailVisible(false);
  };

  const hideEditDialog = () => {
    setEditVisible(false);
  };

  const hidePaymentDialog = () => {
    setPaymentVisible(false);
  };

  const onRowEditComplete = async (e: DataTableRowEditCompleteEvent) => {
    let { newData, index } = e;
    var updatedOrderDetail = null;
    updatedOrderDetail = data.findIndex(
      (item: { OrderModel: any; orderDetail: OrderDetail[] }) =>
        item.orderDetail.some(
          (detail) => detail.orderDetailID === newData.orderDetailID
        )
    );
    if (updatedOrderDetail != null) {
      const previousData = { ...data[updatedOrderDetail].orderDetail[index] };
      
      const pendingQuantity = newPendingQuantity[newData.orderDetailID] ?? newData.pendingQuantity;
      data[updatedOrderDetail].orderDetail[index].pendingQuantity = pendingQuantity;
      try {
        let response = await OrderService.updatePendingQuantityOfOrderDetail(
          newData.orderDetailID,
          0,
          newData.pendingQuantity
        );
        if (response.id >= 0) {
          setNewPendingQuantity((prev) => {const { [newData.orderDetailID]: _, ...rest } = prev;
          return rest;
          });
          showSimpleToastMessage("Success", response.message, "success");
        } else {
          showSimpleToastMessage(
            "Error",
            "Error in updating quantity of order detail",
            "error"
          );
          data[updatedOrderDetail].orderDetail[index] = previousData;
        }
      } catch {
        showSimpleToastMessage(
          "Error",
          "Error in updating quantity of order detail",
          "error"
        );
        data[updatedOrderDetail].orderDetail[index] = previousData;
      }
    }
  };

  const rowEditor = (options: ColumnEditorOptions) => {
    if (options.column.props.header == "Pending Quantity") {
      return (
        <InputNumber
          value={newPendingQuantity[options.rowData.orderDetailID] ?? parseFloat(options.value)}
          style={{
            border: "1px solid #090",
          }}
          size={3}
          onValueChange={(e: InputNumberValueChangeEvent) => {
            setNewPendingQuantity((prev) => ({ ...prev,[options.rowData.orderDetailID]: e.value ?? 0,}));
            options.editorCallback!(e.value?.toString());
          }}
          minFractionDigits={0}
          maxFractionDigits={5}
        />
      );
    } else return options.value;
  };

  const rowExpansionTemplate = (data: OrderModel) => {
    return (
        <div className="p-3">            
            <div className="border-1 flex">              
              <div style={{width:'75%'}}>
              <DataTable value={data.orderDetail} editMode="row" onRowEditComplete={onRowEditComplete}>
                {expandedColumns.map((col, i) => (
                  <Column
                    key={i}
                    field={col.cell}
                    header={col.header}
                    style={col.style}
                    editor={(options) => rowEditor(options)}                    
                    body={(rowData, { rowIndex }) => col.cell(rowData, { rowIndex })} // Pass rowIndex here.
                  />
                ))}
                <Column
                 rowEditor
                 header="Action"
                 headerStyle={{ textAlign: "right" }}
                />                
              </DataTable>
              </div>
              <div style={{width:'25%'}}>
                {/* <Button label="Generate Invoice" className="border-round mt-2 mr-2 ml-5" onClick={(e) => handleCreateInvoice(e, data, 1)} style={{ width: "80%", textAlign:'center' }} severity="success"/><br/> */}
                <Button className="border-round mt-2 mr-2 ml-5" onClick={() => handleSetEditInvoice(data)} style={{ width: "80%", textAlign:'center' }} severity="info">
                  {t("invoices_create_edit_button")}
                </Button>
              </div>
            </div>
        </div>
    );
  };

  const handleRowToggle = (e: { data: DataTableExpandedRows }) => {
    setExpandedRows(e.data);
  }

  return (
    <>
      <Toast ref={toast} position="top-right" />
      <div className="col-12">
        <div className="card border-noround p-2">
          <DataTable
            value={dataRecords}
            expandedRows={expandedRows} 
            onRowToggle={handleRowToggle}
            dataKey="orderID"
            rowExpansionTemplate={rowExpansionTemplate}
            paginator rows={10}
            rowsPerPageOptions={[5, 10, 25, 50, 100]}
            stripedRows
            emptyMessage="No order found."
            paginatorTemplate="RowsPerPageDropdown FirstPageLink PrevPageLink CurrentPageReport NextPageLink LastPageLink"
            currentPageReportTemplate="{first} to {last} of {totalRecords}"
            editMode="row"
          >
            {columns.map((col, i) => (
              <Column
                key={i}
                expander={col.expander}
                field={col.field}
                header={col.header}
                style={col.style}
                body={col.cell}
              />
            ))}
          </DataTable>
        </div>
        {paymentOrder !== undefined || paymentOrder !== null ? (
          <Dialog
            header="Payment"
            visible={paymentVisible}
            style={{ width: "67vw" }}
            onHide={hidePaymentDialog}
            className="orderlist-dialog">
            <PaymentProcess runHideDialog={hidePaymentDialog} runOrderPayment={handlePayment} handleOtherPaymentMode={handleOtherPaymentMode} OrderData={paymentOrder} />
          </Dialog>
        ) : null}
        {editOrder !== undefined || editOrder !== null ? (
          <Dialog
            header={t("generateInv_edit_label")}
            visible={editVisible}
            style={{ width: "67vw" }}
            onHide={hideEditDialog}
            className="orderlist-dialog">
            <CreateInvoice runHideDialog={hideEditDialog} runCreateInvoice={handleCreateInvoice} OrderData={editOrder} setOrderData={setEditOrder} setSelectedOrder={setEditOrder}/>
          </Dialog>
        ) : null}
        {selectedOrder !== undefined || selectedOrder !== null ? (
          <Dialog
            header={t("orderlist_orderdetails_label")}
            visible={detailVisible}
            style={{ width: "67vw" }}
            onHide={hideDetailDialog}
            className="orderlist-dialog">
            <OrderReview OrderData={selectedOrder} />
          </Dialog>
        ) : null}
      </div>
    </>
  );
};
export default InvoicesDataTable;
